import { useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useDeployContext } from "@/contexts/DeployContext";
import { useFlow } from "@/contexts/FlowContext";
import { useFlowState } from "@/hooks/useFlowState";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { useCurrentWorkflow, useCurrentWorkspace } from "@/store";
import { Flows, DeploySteps } from "@/types/flows";
import { AnalyticsFlowEvent } from "@/analytics/types";

/**
 * Custom hook that handles user sign-in and workflow creation for Rapid.
 * Returns a function that triggers the login flow and creates a workflow upon successful authentication.
 */
export function useSignInAndCreateWorkflow() {
    const { task } = useFlow();
    const { setStep } = useFlowState();
    const { user, requireLogin, activeSession } = useAuth();
    const { createRapidWorkflowDeploy, creatingRapidWorkflow } = useDeployContext();
    const { currentPromptingData, currentInferenceSampledData } = useComputedValues();
    const { track } = useAnalytics();

    const currentWorkflow = useCurrentWorkflow();
    const currentWorkspace = useCurrentWorkspace();

    const createRapidWorkflow = useCallback(async () => {
        if (user && activeSession && currentWorkspace?.id && !currentWorkflow) {
            try {
                const workflow = await createRapidWorkflowDeploy({
                    task,
                    workspaceId: currentWorkspace.id,
                    prompts: currentPromptingData,
                    inferenceData: currentInferenceSampledData
                });
                setStep({
                    flow: Flows.DEPLOY,
                    step: DeploySteps.DEPLOY,
                    workspaceUrl: currentWorkspace.url,
                    workflowUrl: workflow.url
                });
            } catch (err) {
                console.error("useSignInAndCreateWorkflow error", err);
                throw new Error(
                    `There was an error creating your workflow. Please try again. ${err}`
                );
            }
        }
    }, [
        activeSession,
        createRapidWorkflowDeploy,
        currentInferenceSampledData,
        currentPromptingData,
        currentWorkspace,
        currentWorkflow,
        setStep,
        task,
        user
    ]);

    const {
        isLoading: isLoadingCreateRapidWorkflow,
        error: errorCreateRapidWorkflow,
        invoke: invokeCreateRapidWorkflow
    } = useAsyncBoundOperation(createRapidWorkflow, { autoInvoke: true });

    const signInAndCreateWorkflow = useCallback(() => {
        track({ event: AnalyticsFlowEvent.GET_API_KEY_CLICKED, data: { task } });
        requireLogin(invokeCreateRapidWorkflow);
    }, [invokeCreateRapidWorkflow, requireLogin, task, track]);

    const loading = creatingRapidWorkflow || isLoadingCreateRapidWorkflow;

    return { signInAndCreateWorkflow, loading, error: errorCreateRapidWorkflow };
}
